import { cs } from '../../lib/currency';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Search, Loader2, AlertTriangle, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Last-resort reconciliation tool with two modes:
 *   - "By PI ID": admin pastes pi_/cs_/ch_ → we map and mark paid.
 *   - "By Customer Email": admin enters email → we list every recent
 *     PaymentIntent + show whether we already have a matching DB record.
 *     One-click reconcile per row, no copy/paste of fragile IDs.
 */
export default function ReconcileByPIDialog({ open, onOpenChange, onSuccess }) {
  const [mode, setMode] = useState('by-id');
  const [piId, setPiId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [reconcilingId, setReconcilingId] = useState(null);
  const [result, setResult] = useState(null);
  const [emailResults, setEmailResults] = useState(null);
  const [stripeEnv, setStripeEnv] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [diagnose, setDiagnose] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [bulkReconciling, setBulkReconciling] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/admin/payments/stripe-environment')
      .then((r) => { if (!cancelled) setStripeEnv(r.data); })
      .catch(() => { if (!cancelled) setStripeEnv(null); });
    return () => { cancelled = true; };
  }, [open]);

  const reset = () => {
    setPiId(''); setEmail(''); setResult(null);
    setEmailResults(null); setReconcilingId(null);
    setDiagnose(null);
  };

  const runDiagnose = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setDiagnosing(true);
    setDiagnose(null);
    try {
      const { data } = await api.get(`/admin/payments/diagnose-by-email?email=${encodeURIComponent(trimmed)}`);
      setDiagnose(data);
      toast.success('Diagnostic complete — see panel below');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Diagnose failed';
      toast.error(typeof msg === 'string' ? msg : 'Diagnose failed');
    } finally {
      setDiagnosing(false);
    }
  };

  const runBulkReconcile = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    if (!window.confirm(`Reconcile EVERY succeeded Stripe PaymentIntent for ${trimmed}? Idempotent — safe to re-run.`)) {
      return;
    }
    setBulkReconciling(true);
    try {
      const { data } = await api.post('/admin/payments/bulk-reconcile-by-email', { email: trimmed });
      const s = data.summary || {};
      toast.success(`${s.ok || 0} reconciled · ${s.failed || 0} failed · ${s.skipped || 0} skipped (${data.total_pis} total)`);
      onSuccess && onSuccess(data);
      // Re-run search and diagnose to update the displayed state
      await searchByEmail();
      await runDiagnose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Bulk reconcile failed';
      toast.error(typeof msg === 'string' ? msg : 'Bulk reconcile failed');
    } finally {
      setBulkReconciling(false);
    }
  };

  const undoReconcile = async (piIdValue) => {
    if (!piIdValue) return;
    if (!window.confirm(`Undo the reconcile for ${piIdValue}? This will revert ALL records that were marked paid via this PaymentIntent (lane bookings, tournament registrations, payment transactions). The Stripe charge itself is NOT touched.`)) {
      return;
    }
    setUndoing(true);
    try {
      const { data } = await api.post('/admin/payments/undo-reconcile', { payment_intent_id: piIdValue });
      toast.success(data.message || 'Reverted');
      // Surface the same enriched-result panel with the reverted info so
      // the admin can see exactly what got rolled back.
      setResult({
        ok: true,
        data: {
          message: data.message,
          updated: (data.reverted || []).map((r) => ({
            ...r,
            paid_amount: undefined, // not paid anymore
            _undo: true,
          })),
        },
      });
      onSuccess && onSuccess(data);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : (errDetail?.message || 'Undo failed');
      toast.error(msg);
    } finally {
      setUndoing(false);
    }
  };

  const submitById = async () => {
    const trimmed = piId.trim();
    if (!(trimmed.startsWith('pi_') || trimmed.startsWith('cs_') || trimmed.startsWith('ch_'))) {
      toast.error("ID must start with 'pi_' (PaymentIntent), 'cs_' (Session) or 'ch_' (Charge)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/admin/payments/reconcile-by-pi', { payment_intent_id: trimmed });
      setResult({ ok: true, data });
      toast.success(data.message || 'Reconciled');
      onSuccess && onSuccess(data);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : (errDetail?.message || 'Reconcile failed');
      setResult({ ok: false, message: msg, detail: errDetail });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const searchByEmail = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSearching(true);
    setEmailResults(null);
    try {
      const { data } = await api.get(`/admin/payments/find-by-email?email=${encodeURIComponent(trimmed)}`);
      setEmailResults(data);
      if (!data.items?.length) {
        toast.info(`No payments found in Stripe for ${trimmed}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Search failed';
      toast.error(typeof msg === 'string' ? msg : msg.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const reconcileRow = async (piIdValue) => {
    setReconcilingId(piIdValue);
    try {
      const { data } = await api.post('/admin/payments/reconcile-by-pi', { payment_intent_id: piIdValue });
      // iter307bl — Show the admin HOW we matched, especially when the
      // direct ID join failed and we fell back to extended search or
      // fuzzy customer+amount. If it was anything other than 'direct',
      // we surface a hint so the admin can spot flows missing back-link
      // plumbing at the source.
      const via = data.matched_via;
      let msg = data.message || 'Reconciled';
      if (via && via !== 'direct') {
        const label = (
          via === 'stripe_customer_amount_fuzzy' ? 'Matched via Stripe customer + amount (no DB link)' :
          via === 'extended_collection_search' ? 'Matched via extended collection search' :
          `Matched via ${via}`
        );
        msg = `${msg} · ${label}`;
      }
      toast.success(msg);
      onSuccess && onSuccess(data);
      searchByEmail();
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : (errDetail?.message || 'Reconcile failed');
      toast.error(msg);
    } finally {
      setReconcilingId(null);
    }
  };

  // iter307bj — Manually disambiguate a 409 ambiguous_fuzzy_match by
  // posting back with `force_entity_type` + `force_entity_id` so the
  // admin can pick the right candidate without leaving the dialog.
  const applyForceMatch = async (piIdValue, entityType, entityId) => {
    setReconcilingId(piIdValue);
    try {
      const { data } = await api.post('/admin/payments/reconcile-by-pi', {
        payment_intent_id: piIdValue,
        force_entity_type: entityType,
        force_entity_id: entityId,
      });
      // iter307bl — Same matched_via hint on the force-pick path.
      let msg = data.message || `Reconciled to ${entityType}`;
      if (data.matched_via && data.matched_via !== 'direct') {
        msg = `${msg} · Matched via ${data.matched_via.replace(/_/g, ' ')}`;
      }
      toast.success(msg);
      onSuccess && onSuccess(data);
      // Re-run searches so the dialog state stays consistent
      if (email) searchByEmail();
      setResult(null);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : (errDetail?.message || 'Force-match failed');
      toast.error(msg);
    } finally {
      setReconcilingId(null);
    }
  };

  const fmtTime = (ts) => ts ? new Date(ts * 1000).toLocaleString() : '—';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reconcile-by-pi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Reconcile Stripe Payment
          </DialogTitle>
          <DialogDescription>
            Mark a record paid using either a Stripe ID or a customer email lookup.
          </DialogDescription>
          {stripeEnv && (
            <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="stripe-env-badge">
              <span className="text-xs text-slate-500">Connected Stripe environment:</span>
              <Badge className={
                stripeEnv.stripe_mode === 'live'
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-amber-100 text-amber-700 border-amber-200'
              }>
                {stripeEnv.stripe_mode?.toUpperCase() || 'UNKNOWN'}
              </Badge>
              {stripeEnv.account_id && (
                <code className="text-xs text-slate-500">{stripeEnv.account_id}</code>
              )}
              {stripeEnv.stripe_mode === 'TEST' && (
                <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Backend is on TEST keys — live Stripe IDs will not be found here.
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList>
            <TabsTrigger value="by-id" data-testid="tab-by-id">By Stripe ID</TabsTrigger>
            <TabsTrigger value="by-email" data-testid="tab-by-email">By Customer Email</TabsTrigger>
          </TabsList>

          <TabsContent value="by-id" className="space-y-3 pt-2">
            <div className="space-y-1">
              <Label htmlFor="pi-id">PaymentIntent / Session / Charge ID</Label>
              <Input
                id="pi-id"
                value={piId}
                onChange={(e) => setPiId(e.target.value)}
                placeholder="pi_3TPuxk... or cs_live_... or ch_..."
                data-testid="pi-id-input"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Tip: copy directly from Stripe Dashboard → Payments. Watch for l↔1 and O↔0 typos.
              </p>
            </div>
            {result && result.ok && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2 text-sm" data-testid="reconcile-success-panel">
                <div className="font-medium text-emerald-800">{result.data.message}</div>
                {result.data.updated?.map((u, i) => {
                  if (u._undo) {
                    return (
                      <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        Reverted → <code>{u.entity_type}</code>
                        {u.player_name && <span> · {u.player_name}</span>}
                        {u.entity_id && <span className="ml-1 text-slate-500">({u.entity_id?.slice(0, 8)}…)</span>}
                      </div>
                    );
                  }
                  return (
                    <div key={i} className="text-xs text-emerald-900 bg-white/60 rounded px-2 py-1.5 border border-emerald-100">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{u.entity_type}</Badge>
                        {u.matched_via === 'fallback_re_register' && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]" title="Stripe metadata pointed to a deleted registration; matched a re-created replacement via (tournament_id, parent_id, amount).">
                            re-register fallback
                          </Badge>
                        )}
                        {u.paid_amount !== undefined && (
                          <span className="font-semibold text-emerald-700">{cs()}{Number(u.paid_amount || 0).toFixed(2)}</span>
                        )}
                      </div>
                      {/* Tournament: parent · player · tournament — age */}
                      {u.entity_type === 'tournament_registration' && (
                        <div className="text-slate-700 mt-0.5 leading-snug">
                          <span className="font-medium">{u.parent_name || '—'}</span>
                          {u.parent_email && <span className="text-slate-500"> · {u.parent_email}</span>}
                          <br />
                          {u.player_name && <span>{u.player_name}</span>}
                          {u.tournament_name && <span> · {u.tournament_name}</span>}
                          {u.age_category && <span className="text-slate-500"> · {u.age_category}</span>}
                        </div>
                      )}
                      {/* Lane booking: parent · lane · date+time */}
                      {u.entity_type === 'lane_booking' && (
                        <div className="text-slate-700 mt-0.5 leading-snug">
                          <span className="font-medium">{u.parent_name || '—'}</span>
                          {u.parent_email && <span className="text-slate-500"> · {u.parent_email}</span>}
                          <br />
                          {u.lane_name && <span>{u.lane_name}</span>}
                          {u.date && <span> · {u.date}</span>}
                          {u.start_time && <span> at {u.start_time}</span>}
                        </div>
                      )}
                      {/* Generic txn: surface underlying entity if present */}
                      {u.entity_type === 'payment_transaction' && (
                        <div className="text-slate-600 mt-0.5 leading-snug">
                          {u.underlying_entity_type && (
                            <>
                              Underlying: <code>{u.underlying_entity_type}</code>{' '}
                              <span className="text-slate-500">({u.underlying_entity_id?.slice(0, 8)}…)</span>{' '}
                              {u.underlying_orphan ? (
                                <Badge className="bg-red-100 text-red-700 text-[10px] ml-1">missing — likely deleted</Badge>
                              ) : (u.underlying_updated || 0) === 0 ? (
                                <Badge className="bg-amber-100 text-amber-800 text-[10px] ml-1">already paid / not updated</Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-1">linked OK</Badge>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">id: {u.entity_id}</div>
                    </div>
                  );
                })}
                {/* Backend-surfaced warnings (e.g., orphan underlying entity) */}
                {result.data.warnings?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {result.data.warnings.map((w, idx) => (
                      <div key={idx} className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>{w.message}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Undo button — only show for SUCCESSFUL reconciles, not for an undo result */}
                {!result.data.updated?.some((u) => u._undo) && piId && (
                  <div className="pt-1 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-200 hover:bg-amber-50"
                      onClick={() => undoReconcile(piId.trim())}
                      disabled={undoing}
                      data-testid="reconcile-undo-btn"
                    >
                      {undoing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5 mr-1" />}
                      Wrong row? Undo this reconcile
                    </Button>
                  </div>
                )}
              </div>
            )}
            {result && !result.ok && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <div className="font-medium mb-1">Couldn't reconcile</div>
                <div className="text-xs whitespace-pre-wrap">{result.message}</div>
                {/* iter307bj — When the backend returns 409 with
                    `ambiguous_fuzzy_match` candidates, render a picker
                    so admin can disambiguate without leaving the
                    dialog. Clicking a row re-submits with
                    force_entity_type + force_entity_id. */}
                {result.detail?.code === 'ambiguous_fuzzy_match' && Array.isArray(result.detail?.candidates) && result.detail.candidates.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid="ambiguous-candidates">
                    <div className="text-[11px] font-semibold uppercase text-red-800 tracking-wide">Pick the right record:</div>
                    {result.detail.candidates.map((c, i) => (
                      <div key={c.id || i} className="flex items-center justify-between gap-2 bg-white border border-red-200 rounded px-2.5 py-1.5">
                        <div className="text-xs text-slate-700 min-w-0 truncate">
                          <span className="font-mono text-[10px] bg-slate-100 rounded px-1 py-0.5 mr-1.5">{c.entity_type}</span>
                          <span className="font-medium">{cs()}{(c.amount ?? 0).toFixed ? c.amount.toFixed(2) : c.amount}</span>
                          {c.description && <span className="text-slate-500 ml-1.5">· {c.description}</span>}
                          <span className="text-slate-400 ml-1.5 font-mono text-[10px]">{c.id?.slice(0, 8)}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] shrink-0"
                          disabled={reconcilingId === piId}
                          onClick={() => applyForceMatch(piId, c.entity_type, c.id)}
                          data-testid={`force-match-${c.entity_type}-${c.id}`}
                        >
                          {reconcilingId === piId ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button onClick={submitById} disabled={loading || !piId.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="reconcile-submit">
                {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Reconcile
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="by-email" className="space-y-3 pt-2">
            <div className="space-y-1">
              <Label htmlFor="cust-email">Customer Email</Label>
              <div className="flex gap-2">
                <Input
                  id="cust-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchByEmail()}
                  placeholder="parent@example.com"
                  data-testid="cust-email-input"
                />
                <Button onClick={searchByEmail} disabled={searching || !email.trim()} variant="default" data-testid="search-email-submit">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                  Search
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                We'll look up Stripe customers by email and list their recent PaymentIntents — including guest checkout sessions.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={runDiagnose}
                  disabled={diagnosing || !email.trim()}
                  data-testid="diagnose-email-btn"
                  className="text-blue-700 border-blue-200 hover:bg-blue-50"
                >
                  {diagnosing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
                  Diagnose (show DB + Stripe state)
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={runBulkReconcile}
                  disabled={bulkReconciling || !email.trim()}
                  data-testid="bulk-reconcile-email-btn"
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                >
                  {bulkReconciling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  Reconcile ALL PIs for this email
                </Button>
              </div>
            </div>

            {diagnose && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2 text-xs" data-testid="diagnose-panel">
                <div className="font-medium text-blue-900 flex items-center gap-2">
                  <Search className="h-3.5 w-3.5" /> Diagnostic Snapshot
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-slate-700">
                  <div className="bg-white border rounded p-2" data-testid="diag-card-tournaments">
                    <div className="text-slate-500 text-[10px] uppercase">Tournament Regs</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.tournament_regs_count || 0}</div>
                    <div className="text-amber-700">{diagnose.summary?.tournament_regs_pending_or_overdue || 0} pending/overdue</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-lanes">
                    <div className="text-slate-500 text-[10px] uppercase">Lane Bookings</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.lane_bookings_count || 0}</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-txns">
                    <div className="text-slate-500 text-[10px] uppercase">Payment Txns</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.payment_txns_count || 0}</div>
                  </div>
                  {/* iter307bg — Surface the previously-hidden categories. */}
                  <div className="bg-white border rounded p-2" data-testid="diag-card-enrollments">
                    <div className="text-slate-500 text-[10px] uppercase">Enrollments</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.enrollments_count || 0}</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-monthly-invoices">
                    <div className="text-slate-500 text-[10px] uppercase">Monthly Invoices</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.monthly_invoices_count || 0}</div>
                    {diagnose.summary?.monthly_invoices_open > 0 && (
                      <div className="text-amber-700">{diagnose.summary.monthly_invoices_open} open</div>
                    )}
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-small-groups">
                    <div className="text-slate-500 text-[10px] uppercase">Small Group Inv</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.small_group_invoices_count || 0}</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-camps">
                    <div className="text-slate-500 text-[10px] uppercase">Camp Regs</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.camp_registrations_count || 0}</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-manual">
                    <div className="text-slate-500 text-[10px] uppercase">Manual Inv</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.manual_invoices_count || 0}</div>
                  </div>
                  <div className="bg-white border rounded p-2" data-testid="diag-card-stripe-pis">
                    <div className="text-slate-500 text-[10px] uppercase">Stripe PIs</div>
                    <div className="text-sm font-semibold">{diagnose.summary?.stripe_pis_count || 0}</div>
                    {diagnose.summary?.stripe_pis_no_db_match > 0 && (
                      <div className="text-red-700">{diagnose.summary.stripe_pis_no_db_match} unmatched</div>
                    )}
                  </div>
                </div>
                {/* Tournament rows with their display status — this is the source of the "still overdue" complaint */}
                {diagnose.tournament_registrations?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mt-2 mb-1">Tournament Registrations</div>
                    <div className="max-h-48 overflow-y-auto bg-white border rounded divide-y">
                      {diagnose.tournament_registrations.map((r) => (
                        <div key={r.id} className="p-2 text-[11px]" data-testid={`diag-tr-${r.id}`}>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className={
                              r.display_status === 'paid' ? 'bg-emerald-100 text-emerald-700 text-[10px]' :
                              r.display_status === 'overdue' ? 'bg-red-100 text-red-700 text-[10px]' :
                              r.display_status === 'pending' ? 'bg-amber-100 text-amber-700 text-[10px]' :
                              'bg-slate-100 text-slate-700 text-[10px]'
                            }>{r.display_status}</Badge>
                            <span className="font-medium">{r.tournament_name || 'Tournament'}</span>
                            {r.player_name && <span className="text-slate-500">· {r.player_name}</span>}
                            {r.would_show_in_invoice_pending && (
                              <Badge className="bg-amber-100 text-amber-800 text-[9px]">in pending list</Badge>
                            )}
                          </div>
                          <div className="text-slate-500 mt-0.5">
                            payment_status=<code>{r.payment_status_raw || 'null'}</code>
                            {' · '}paid_at=<code>{r.paid_at || 'null'}</code>
                            {' · '}amount={cs()}{(r.payment_amount || 0).toFixed(2)}
                          </div>
                          <div className="text-slate-400 mt-0.5 font-mono text-[10px] truncate">
                            id: {r.id}
                            {r.stripe_session_id && <> · sess: {r.stripe_session_id.slice(0, 14)}…</>}
                            {r.stripe_payment_intent_id && <> · pi: {r.stripe_payment_intent_id.slice(0, 14)}…</>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Stripe PIs and what they match */}
                {diagnose.stripe_payment_intents?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mt-2 mb-1">Stripe PaymentIntents → DB Match</div>
                    <div className="max-h-48 overflow-y-auto bg-white border rounded divide-y">
                      {diagnose.stripe_payment_intents.map((p) => (
                        <div key={p.payment_intent_id} className="p-2 text-[11px]" data-testid={`diag-pi-${p.payment_intent_id}`}>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className={
                              p.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700 text-[10px]' :
                              'bg-slate-100 text-slate-700 text-[10px]'
                            }>{p.status}</Badge>
                            <code className="text-[10px]">{p.payment_intent_id}</code>
                            <span className="font-semibold">{cs()}{p.amount.toFixed(2)}</span>
                            <Badge className={
                              p.db_match === 'no_match' ? 'bg-red-100 text-red-700 text-[10px]' :
                              'bg-blue-100 text-blue-700 text-[10px]'
                            }>{p.db_match}</Badge>
                          </div>
                          {p.db_match_id && (
                            <div className="text-slate-500 mt-0.5 font-mono text-[10px] truncate">→ {p.db_match_id}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {emailResults && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                  <span>{emailResults.total_payment_intents} payment(s) · {emailResults.matched_customers} customer(s) matched</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {emailResults.items?.length ? emailResults.items.map((it) => {
                    const linked = it.linked_record;
                    return (
                      <div key={it.payment_intent_id} className="p-3 flex items-center gap-3" data-testid={`pi-row-${it.payment_intent_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-slate-700 truncate">{it.payment_intent_id}</code>
                            <Badge className={
                              it.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700 text-[10px]' :
                              it.status === 'processing' ? 'bg-blue-100 text-blue-700 text-[10px]' :
                              'bg-slate-100 text-slate-700 text-[10px]'
                            }>{it.status}</Badge>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {it.currency} {cs()}{it.amount.toFixed(2)} · {fmtTime(it.created)}
                            {it.description ? ` · ${it.description}` : ''}
                          </div>
                          {linked && (
                            <div className="text-xs text-slate-600 mt-1 inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                              Linked to {linked.entity_type} <code>{linked.id?.slice(0, 8)}…</code>
                              {linked.payment_status && <Badge variant="outline" className="text-[10px] ml-1">{linked.payment_status}</Badge>}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant={linked?.payment_status === 'paid' ? 'outline' : 'default'}
                          disabled={it.status !== 'succeeded' || reconcilingId === it.payment_intent_id || linked?.payment_status === 'paid'}
                          onClick={() => reconcileRow(it.payment_intent_id)}
                          data-testid={`reconcile-row-${it.payment_intent_id}`}
                          className="shrink-0"
                        >
                          {reconcilingId === it.payment_intent_id ? <Loader2 className="h-4 w-4 animate-spin" /> :
                           linked?.payment_status === 'paid' ? 'Already paid' : 'Reconcile'}
                        </Button>
                      </div>
                    );
                  }) : (
                    <div className="p-6 text-center text-sm text-slate-500">No payments found.</div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reconcile-cancel">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
